import { useEffect, useRef, useState, type KeyboardEvent } from 'react'
import { api, type LookupItem } from '../api/client'
import { Input } from './controls'

/**
 * Поле поиска позиции (А-902, А-1302).
 *
 * Мгновенный поиск, приём ввода со сканера штрихкодов, подтверждение
 * клавишей ввода. При единственном точном совпадении по артикулу или
 * штрихкоду позиция выбирается сразу: сканер вводит код и нажимает Enter,
 * промежуточный выбор из списка сделал бы работу со сканером невозможной.
 */
export default function ProductPicker({
  warehouseId,
  onPick,
  placeholder = 'Артикул, штрихкод или название',
  autoFocus,
}: {
  warehouseId?: number
  onPick: (item: LookupItem) => void
  placeholder?: string
  autoFocus?: boolean
}) {
  const [query, setQuery] = useState('')
  const [items, setItems] = useState<LookupItem[]>([])
  const [active, setActive] = useState(0)
  const [open, setOpen] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (query.trim() === '') {
      setItems([])
      setOpen(false)
      return
    }
    const t = setTimeout(() => {
      void api.stockLookup(query, warehouseId).then((list) => {
        setItems(list)
        setActive(0)
        setOpen(list.length > 0)
      })
    }, 150)
    return () => clearTimeout(t)
  }, [query, warehouseId])

  function pick(item: LookupItem) {
    onPick(item)
    setQuery('')
    setItems([])
    setOpen(false)
    // Фокус возвращается в поле поиска: следующая позиция вводится
    // без обращения к мыши.
    inputRef.current?.focus()
  }

  function onKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setActive((i) => Math.min(i + 1, items.length - 1))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setActive((i) => Math.max(i - 1, 0))
    } else if (e.key === 'Enter') {
      e.preventDefault()
      if (items.length === 0) return
      // Единственное точное совпадение — ввод со сканера.
      const exact = items.filter((i) => i.exact)
      pick(exact.length === 1 ? exact[0] : items[active])
    } else if (e.key === 'Escape') {
      setOpen(false)
    }
  }

  return (
    <div className="relative">
      <Input
        ref={inputRef}
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        onKeyDown={onKeyDown}
        onFocus={() => setOpen(items.length > 0)}
        placeholder={placeholder}
        autoFocus={autoFocus}
        aria-label="Поиск позиции"
        autoComplete="off"
      />

      {open && (
        <ul className="absolute z-20 mt-1 max-h-72 w-full overflow-y-auto rounded-md border border-ink-200 bg-white shadow-lg dark:border-ink-700 dark:bg-ink-900">
          {items.map((item, i) => (
            <li key={item.product_id}>
              <button
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => pick(item)}
                onMouseEnter={() => setActive(i)}
                className={`flex w-full items-center gap-3 px-3 py-2 text-left text-sm ${
                  i === active ? 'bg-brand-50 dark:bg-brand-500/15' : ''
                }`}
              >
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-ink-800 dark:text-ink-200">{item.name}</span>
                  <span className="font-mono text-xs text-ink-500">
                    {item.sku}
                    {item.barcode && ` · ${item.barcode}`}
                  </span>
                </span>
                <span className="shrink-0 text-right text-xs">
                  <span className="block tabular-nums text-ink-700 dark:text-ink-300">
                    {item.price.toLocaleString('ru-RU')} ₸
                  </span>
                  <span
                    className={`block tabular-nums ${
                      item.stock > 0 ? 'text-success-700 dark:text-success-100' : 'text-ink-400'
                    }`}
                  >
                    остаток {item.stock}
                  </span>
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
